import React, { useEffect, useRef, useState } from 'react';
import { Portal, IconButton, CircularProgress, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Close as ClearIcon,
    Info as InfoIcon,
    ErrorOutline as ErrorIcon,
    Assessment as SummaryIcon,
    AccountBalance as BalancesIcon,
    Receipt as ChargesIcon
} from '@mui/icons-material';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import ledgerReportsService from '../../../Services/ledgerReports.service';
import { useTranslation } from 'react-i18next';
import s from './LedgerReportsModal.module.scss';

const LedgerReportsModal = ({ visible, onClose, client, isLeadingBusiness = false, selectedClient = null }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const isRtl = direction === 'rtl';
    const modalRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [reportType, setReportType] = useState('summary');
    const [format, setFormat] = useState('pdf');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(new Date());

    useEffect(() => {
        if (visible) modalRef.current?.focus();
    }, [visible]);

    const reportTypes = [
        {
            value: 'charges',
            label: t('ChargesReport'),
            description: t('ChargesReportDescription'),
            icon: <ChargesIcon fontSize="small" />,
            tone: s.toneRed
        },
        {
            value: 'balances',
            label: t('BalancesPaymentsReport'),
            description: t('BalancesPaymentsReportDescription'),
            icon: <BalancesIcon fontSize="small" />,
            tone: s.toneGreen
        },
        {
            value: 'summary',
            label: t('SummaryReport'),
            description: t('SummaryReportDescription'),
            icon: <SummaryIcon fontSize="small" />,
            tone: s.toneBlue
        },
    ];

    const formats = [
        { value: 'pdf', label: t('PDF'), icon: <PdfIcon fontSize="small" />, tone: s.toneRed },
        { value: 'excel', label: t('Excel'), icon: <ExcelIcon fontSize="small" />, tone: s.toneGreen }
    ];

    const dateRangeInvalid = Boolean(startDate && endDate && startDate.getTime() > endDate.getTime());

    const handleDownload = async () => {
        if (!endDate) {
            return;
        }

        setLoading(true);
        try {
            let blob;
            const endDateValue = endDate;
            const startDateValue = startDate ? startDate : null;

            // If leading business with a specific client selected, treat as single client report
            if (isLeadingBusiness && selectedClient) {
                blob = await downloadSingleReportForSelectedClient(reportType, format, startDateValue, endDateValue);
            } else if (isLeadingBusiness) {
                blob = await downloadBulkReport(reportType, format, startDateValue, endDateValue);
            } else {
                blob = await downloadSingleReport(reportType, format, startDateValue, endDateValue);
            }

            const dateStr = endDate.toLocaleDateString('en-GB');
            const startStr = startDate ? startDate.toLocaleDateString('en-GB') : '';
            const periodStr = startDate ? `${startStr}_to_${dateStr}` : `until_${dateStr}`;

            // For Excel, use 'xlsx' as the file extension
            let fileExtension = format === 'excel' ? 'xlsx' : format;

            let filename;
            if (isLeadingBusiness && selectedClient) {
                // Single client report from leading business
                filename = `דוח-${getReportTypeName(reportType)}-${selectedClient.value}-${periodStr}.${fileExtension}`;
            } else if (isLeadingBusiness) {
                filename = `דוח-${getReportTypeName(reportType)}-מרוכז-${client?.accountantId}-${periodStr}.${fileExtension}`;
            } else {
                filename = `דוח-${getReportTypeName(reportType)}-${client?.caseId}-${periodStr}.${fileExtension}`;
            }

            ledgerReportsService.downloadFile(blob, filename);
            onClose();
        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setLoading(false);
        }
    };

    const downloadSingleReport = async (type, format, startDate, endDate) => {
        const caseId = client?.caseId;

        if (format === 'pdf') {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportPDF(caseId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportPDF(caseId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportPDF(caseId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        } else {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportExcel(caseId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportExcel(caseId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportExcel(caseId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        }
    };

    const downloadSingleReportForSelectedClient = async (type, format, startDate, endDate) => {
        const caseId = selectedClient?.value;

        if (format === 'pdf') {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportPDF(caseId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportPDF(caseId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportPDF(caseId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        } else {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportExcel(caseId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportExcel(caseId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportExcel(caseId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        }
    };

    const downloadBulkReport = async (type, format, startDate, endDate) => {
        const accountantId = client?.accountantId;

        if (format === 'pdf') {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportBulkPDF(accountantId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportBulkPDF(accountantId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportBulkPDF(accountantId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        } else {
            switch (type) {
                case 'summary':
                    return await ledgerReportsService.downloadSummaryReportBulkExcel(accountantId, startDate, endDate);
                case 'balances':
                    return await ledgerReportsService.downloadBalancesPaymentsReportBulkExcel(accountantId, startDate, endDate);
                case 'charges':
                    return await ledgerReportsService.downloadChargesReportBulkExcel(accountantId, startDate, endDate);
                default:
                    throw new Error('Invalid report type');
            }
        }
    };

    const getReportTypeName = (type) => {
        switch (type) {
            case 'summary':
                return 'סיכום-תנועות-כספיות';
            case 'balances':
                return 'יתרות-ותשלומים';
            case 'charges':
                return 'חיובים';
            default:
                return 'דוח';
        }
    };

    const handleClose = () => {
        setReportType('summary');
        setFormat('pdf');
        setStartDate(null);
        setEndDate(new Date());
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
            handleClose();
        }
    };

    if (!visible) return null;

    const title = isLeadingBusiness && selectedClient
        ? `${t('GenerateLedgerReport')} - ${selectedClient.label}`
        : isLeadingBusiness
            ? t('GenerateLedgerReports')
            : t('GenerateLedgerReport');

    const selectedType = reportTypes.find((type) => type.value === reportType);
    const selectedFormat = formats.find((fmt) => fmt.value === format);
    const canGenerate = Boolean(endDate) && !dateRangeInvalid && !loading;

    const infoStats = isLeadingBusiness
        ? [
            { label: t('AccountantID'), value: client?.accountantId },
            { label: t('AccountantName'), value: client?.name },
        ]
        : [
            { label: t('ClientID'), value: client?.caseId },
            { label: t('ClientName'), value: client?.name },
        ];

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={handleClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
                    <div className={s.modal} ref={modalRef} tabIndex={-1} aria-busy={loading}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Close')}>
                                    <IconButton onClick={handleClose} className={s.closeIcon} aria-label={t('Close')}>
                                        <CancelIcon className={s.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div className={s.headerTitle}>
                                    <span className={s.headerTitleRow}>
                                        <DownloadIcon />
                                        {title}
                                    </span>
                                    {client?.name && (
                                        <span className={s.headerSubtitle}>{client.name}</span>
                                    )}
                                </div>
                                <div className={s.headerSpacer} />
                            </div>

                            <div className={s.scroll}>
                                {/* Client/Accountant Info */}
                                <div className={s.stats}>
                                    {infoStats.map((stat) => (
                                        <div key={stat.label} className={s.statCard}>
                                            <span className={s.statLabel}>{stat.label}</span>
                                            <span className={s.statValue}>{stat.value || '—'}</span>
                                        </div>
                                    ))}
                                </div>
                                {isLeadingBusiness && (
                                    <div className={s.notice}>
                                        <InfoIcon fontSize="small" />
                                        <span>
                                            {selectedClient
                                                ? t('ReportWillIncludeSelectedClient')
                                                : t('ReportWillIncludeAllClients')}
                                        </span>
                                    </div>
                                )}

                                {/* Report Type Selection */}
                                <div className={s.section}>
                                    <div className={s.sectionHeader}>
                                        <h3 className={s.sectionTitle}>{t('LedgerReportType')}</h3>
                                    </div>
                                    <div className={s.typeOptions} role="radiogroup" aria-label={t('LedgerReportType')}>
                                        {reportTypes.map((type) => {
                                            const active = reportType === type.value;
                                            return (
                                                <button
                                                    key={type.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={active}
                                                    className={`${s.option}${active ? ` ${s.optionActive}` : ''}`}
                                                    onClick={() => setReportType(type.value)}
                                                    disabled={loading}
                                                >
                                                    <span className={`${s.optionIcon} ${type.tone}`}>{type.icon}</span>
                                                    <span className={s.optionBody}>
                                                        <span className={s.optionTitle}>{type.label}</span>
                                                        <span className={s.optionDesc}>{type.description}</span>
                                                    </span>
                                                    <span className={s.radioDot} aria-hidden="true" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Format Selection */}
                                <div className={s.section}>
                                    <div className={s.sectionHeader}>
                                        <h3 className={s.sectionTitle}>{t('FileFormat')}</h3>
                                    </div>
                                    <div className={s.formatOptions} role="radiogroup" aria-label={t('FileFormat')}>
                                        {formats.map((fmt) => {
                                            const active = format === fmt.value;
                                            return (
                                                <button
                                                    key={fmt.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={active}
                                                    className={`${s.option} ${s.optionCompact}${active ? ` ${s.optionActive}` : ''}`}
                                                    onClick={() => setFormat(fmt.value)}
                                                    disabled={loading}
                                                >
                                                    <span className={`${s.optionIcon} ${fmt.tone}`}>{fmt.icon}</span>
                                                    <span className={s.optionBody}>
                                                        <span className={s.optionTitle}>{fmt.label}</span>
                                                    </span>
                                                    <span className={s.radioDot} aria-hidden="true" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Date Range Selection */}
                                <div className={s.section}>
                                    <div className={s.sectionHeader}>
                                        <h3 className={s.sectionTitle}>{t('ReportPeriod')}</h3>
                                    </div>
                                    <div className={s.grid}>
                                        <div className={s.field}>
                                            <span className={s.fieldLabel}>
                                                {t('StartDate')}
                                                <span className={s.fieldHint}>({t('Optional')})</span>
                                                {startDate && (
                                                    <Tooltip title={t('Clear')}>
                                                        <IconButton
                                                            size="small"
                                                            className={s.clearBtn}
                                                            onClick={() => setStartDate(null)}
                                                            disabled={loading}
                                                            aria-label={t('Clear')}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </span>
                                            <CustomMonthDatePicker
                                                date={startDate}
                                                onDateChange={setStartDate}
                                                height="40px"
                                                disabled={loading}
                                            />
                                            {!startDate && (
                                                <span className={s.muted}>{t('LedgerReportStartDateHint')}</span>
                                            )}
                                        </div>
                                        <div className={s.field}>
                                            <span className={s.fieldLabel}>
                                                {t('EndDate')}
                                                <span className={s.required}>*</span>
                                            </span>
                                            <CustomMonthDatePicker
                                                date={endDate}
                                                onDateChange={setEndDate}
                                                height="40px"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                    {!endDate && (
                                        <div className={s.errorNote}>
                                            <ErrorIcon fontSize="small" />
                                            {t('PleaseSelectEndDate')}
                                        </div>
                                    )}
                                    {dateRangeInvalid && (
                                        <div className={s.errorNote}>
                                            <ErrorIcon fontSize="small" />
                                            {t('MasavDateRangeInvalid')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={s.footer}>
                                <div className={s.chipRow}>
                                    {selectedType && (
                                        <span className={s.chip}>
                                            {selectedType.icon}
                                            {selectedType.label}
                                        </span>
                                    )}
                                    {selectedFormat && (
                                        <span className={s.chip}>
                                            {selectedFormat.icon}
                                            {selectedFormat.label}
                                        </span>
                                    )}
                                </div>
                                <span className={s.footerSpacer} />
                                <button
                                    type="button"
                                    className={s.secondaryBtn}
                                    onClick={handleClose}
                                    disabled={loading}
                                >
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className={s.primaryBtn}
                                    onClick={handleDownload}
                                    disabled={!canGenerate}
                                >
                                    {loading ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : (
                                        <DownloadIcon fontSize="small" />
                                    )}
                                    {loading ? t('Generating') : t('GenerateReport')}
                                </button>
                            </div>
                        </div>

                        {loading && (
                            <div className={s.loadingOverlay}>
                                <CircularProgress className={s.spinner} size={36} />
                                <span className={s.loadingText}>{t('Generating')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default LedgerReportsModal;
